import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { register, AuthError } from '../lib/auth';
import { AuthShell, AuthBanner, Field } from '../components/AuthShell';
import { validatePassword, PASSWORD_HINT } from '../lib/password-policy';

/**
 * Join an existing workspace. The user types the workspace slug (or name)
 * they were given by their admin and creates an inactive account inside it.
 * A workspace admin must then activate the account from /admin/users before
 * the user can sign in.
 *
 * Workspace creation is intentionally NOT exposed here — only super-admins
 * mint new workspaces (via /super-admin/enterprises). This keeps the public
 * surface area small and prevents random visitors from spawning tenants.
 */
export function Register() {
  const [workspace, setWorkspace] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState<{ workspaceName: string } | null>(null);

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    if (!workspace.trim()) {
      setError('Enter the workspace slug or name you want to join.');
      return;
    }
    const pwError = validatePassword(password);
    if (pwError) {
      setError(pwError);
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      const { workspaceName } = await register(email, password, {
        name: name.trim() || undefined,
        workspace: workspace.trim(),
      });
      setPending({ workspaceName });
    } catch (err) {
      setError(err instanceof AuthError ? err.message : 'Registration failed. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  if (pending) {
    return (
      <AuthShell
        title="Awaiting approval"
        footer={
          <span className="muted">
            <Link to="/login">Back to sign in</Link>
          </span>
        }
      >
        <AuthBanner kind="success">
          Your account has been created in <strong>{pending.workspaceName}</strong>. A workspace
          admin needs to approve it before you can sign in. You'll receive an email when your
          account is activated.
        </AuthBanner>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Join a workspace"
      subtitle="Enter the workspace your admin shared with you. They'll approve your account before you can sign in."
      footer={
        <span className="muted">
          Already have an account? <Link to="/login">Sign in</Link>
        </span>
      }
    >
      {error && <AuthBanner kind="error">{error}</AuthBanner>}
      <form onSubmit={submit}>
        <Field
          label="Workspace slug or name"
          value={workspace}
          onChange={setWorkspace}
          placeholder="acme-corp"
          autoFocus
          required
        />
        <Field label="Your name" value={name} onChange={setName} autoComplete="name" placeholder="Your name" />
        <Field label="Email" type="email" value={email} onChange={setEmail} autoComplete="email" required />
        <Field
          label="Password"
          type="password"
          value={password}
          onChange={setPassword}
          autoComplete="new-password"
          placeholder={PASSWORD_HINT}
          required
        />
        <Field
          label="Confirm password"
          type="password"
          value={confirm}
          onChange={setConfirm}
          autoComplete="new-password"
          required
        />
        <button type="submit" className="btn btn--accent" disabled={busy} style={{ width: '100%', height: 38 }}>
          {busy ? 'Submitting…' : 'Request access'}
        </button>
      </form>
    </AuthShell>
  );
}
